import { Controller } from "@hotwired/stimulus"

import { type DispatchEvent } from "@/entry_points/types"
import { isTopmostElement } from "@/helpers/utils"

export default class ModalController extends Controller<HTMLDivElement> {
  static targets = ["container"]
  declare readonly containerTarget: HTMLDivElement

  private static readonly FADE_IN_DURATION = 100 // See `--fade-in-duration`
  private static readonly KEYDOWN_EVENT = "keydown"
  private static readonly INITIAL_CLASS = "--initial"
  private static readonly NO_STYLE_CLASS = "no-style"
  private static readonly OPEN_CLASS = "--modal-open"
  private static readonly SMOOTH_SCROLL_CLASS = "scroll-smooth"

  private shouldAnimate = true
  private scrollYPosition = 0

  animationOff(): void {
    this.shouldAnimate = false
  }

  animationOn(): void {
    this.shouldAnimate = true
  }

  backgroundClose(e: Event): void {
    // Modal spans entire viewport when open, so checking background click means comparing
    // click target to modal itself.
    if (e.target === this.element) {
      this.close()
    }
  }

  close(e?: KeyboardEvent): void {
    if (e && e.type === ModalController.KEYDOWN_EVENT && e.key === undefined) {
      // this ignores artifical keydown events, such as those
      // sent when using browser auto-fill
      return
    }

    if (this.element.hasAttribute("hidden")) {
      return
    }

    this.setAnimationClass()
    document.body.classList.remove(ModalController.OPEN_CLASS)
    document.removeEventListener("focus", this.focusInModal, true)
    this.element.setAttribute("hidden", "until-found")
    this.restoreScrollPosition()

    this.dispatch("closeModal")
  }

  get containerId(): string {
    return this.containerTarget.id
  }

  open(event?: DispatchEvent<{ modalId?: string }>): void {
    if (event?.detail.modalId && event.detail.modalId !== this.containerId) {
      return
    }

    this.saveScrollPosition()
    this.setAnimationClass()
    document.body.classList.add(ModalController.OPEN_CLASS)
    document.addEventListener("focus", this.focusInModal, true)
    this.element.removeAttribute("hidden")

    // Focus modal on open (this also ensures `keydown` handler works without user interaction).
    // Elements with CSS `visibility: hidden` aren't focusable, so wait for animation.
    window.setTimeout(() => {
      this.containerTarget.focus()
    }, ModalController.FADE_IN_DURATION)

    document.dispatchEvent(new Event(`${this.identifier}:open`))
  }

  useModalView(): void {
    this.element.classList.remove(ModalController.NO_STYLE_CLASS)
  }

  useNoStyleView(): void {
    document.body.classList.remove(ModalController.OPEN_CLASS)
    this.element.classList.add(ModalController.NO_STYLE_CLASS)
    this.element.removeAttribute("hidden")
  }

  private focusInModal = (): void => {
    // To preserve focus within modal (e.g. from tabbing), check if the active element is outside
    // the modal and if so, re-focus the container.
    if (isTopmostElement(this.element) && !this.element.contains(document.activeElement)) {
      this.containerTarget.focus()
    }
  }

  private restoreScrollPosition(): void {
    // Turn off smooth scrolling
    document.documentElement.classList.remove(ModalController.SMOOTH_SCROLL_CLASS)

    // Restore position before modal opened
    window.scrollTo({ top: this.scrollYPosition })

    // Turn smooth scrolling back on
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add(ModalController.SMOOTH_SCROLL_CLASS)
    })
  }

  private saveScrollPosition(): void {
    // CSS applies `position: fixed` when modal opens because iOS reasons. Doing so means we lose
    // page scroll position, so keep track of it to restore it on modal close.
    this.scrollYPosition = Math.max(0, window.scrollY)
    document.body.style.setProperty("--scroll-y-position", `${this.scrollYPosition}px`)
  }

  private setAnimationClass(): void {
    this.element.classList.toggle(ModalController.INITIAL_CLASS, !this.shouldAnimate)
  }
}
